import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import UserAvatar from '../components/UserAvatar';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../utils/api';
import {
  ArrowRight,
  Camera,
  Check,
  Eye,
  EyeOff,
  Layers,
  Mail,
  Scissors,
  ShieldCheck,
  UserCog,
  Users as UsersIcon,
  Video
} from 'lucide-react';

const formatError = (detail) => {
  if (Array.isArray(detail)) return detail.map((item) => item.msg || JSON.stringify(item)).join(' | ');
  if (typeof detail === 'string') return detail;
  return 'Please try again';
};

const EMPTY_FORM = {
  name: '',
  email: '',
  password: '',
  role: 'team_member',
  specialization: 'photo'
};

const ROLES = [
  {
    id: 'team_member',
    label: 'Team member',
    hint: 'Works on assigned tasks and sees their own calendar.',
    icon: UsersIcon
  },
  {
    id: 'media_head',
    label: 'Media head',
    hint: 'Manages events, assigns tasks and tracks the team.',
    icon: UserCog
  }
];
const SPECIALIZATIONS = [
  { id: 'photo', label: 'Photography', icon: Camera },
  { id: 'video', label: 'Videography', icon: Video },
  { id: 'editing', label: 'Editing', icon: Scissors },
  { id: 'other', label: 'Other', icon: Layers }
];

const passwordStrength = (value) => {
  if (!value) return { score: 0, label: '' };
  let score = 0;
  if (value.length >= 6) score += 1;
  if (value.length >= 10) score += 1;
  if (/[A-Z]/.test(value) && /[a-z]/.test(value) && /\d/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value)) score += 1;
  const capped = Math.min(score, 3);
  return { score: capped, label: ['Too short', 'Okay', 'Good', 'Strong'][value.length < 6 ? 0 : capped] };
};

const CreateUser = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(null);

  const set = (key) => (e) => setFormData({ ...formData, [key]: e.target.value });
  const touch = (key) => () => setTouched((prev) => ({ ...prev, [key]: true }));

  const strength = useMemo(() => passwordStrength(formData.password), [formData.password]);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
  const errors = {
    name: !formData.name.trim() ? 'Enter the full name' : '',
    email: !formData.email ? 'Enter an email address' : !emailValid ? 'Enter a valid email address' : '',
    password: !formData.password
      ? 'Set a password'
      : formData.password.length < 6
        ? 'Use at least 6 characters'
        : ''
  };
  const hasErrors = Object.values(errors).some(Boolean);
  const activeRole = ROLES.find((r) => r.id === formData.role);
  const activeSpec = SPECIALIZATIONS.find((s) => s.id === formData.specialization);
  const SpecIcon = activeSpec?.icon;

  const reset = () => {
    setFormData(EMPTY_FORM);
    setTouched({});
    setShowPassword(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (hasErrors) {
      setTouched({ name: true, email: true, password: true });
      toast.error(
        !formData.name || !formData.email || !formData.password
          ? 'Please fill all required fields'
          : errors.password || errors.email || 'Please check the form'
      );
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...formData,
        specialization: formData.role === 'team_member' ? formData.specialization : null
      };
      await api.post('/auth/register', payload);
      toast.success('User created successfully');
      setCreated({ name: formData.name.trim(), email: formData.email });
      reset();
    } catch (error) {
      toast.error('Failed to create user', {
        description: formatError(error.response?.data?.detail)
      });
    } finally {
      setLoading(false);
    }
  };

  const fieldClass = (key) => `field-input ${touched[key] && errors[key] ? 'has-error' : ''}`;

  return (
    <Layout>
      <div className="dashboard-page institutions-page">
        <div className="dashboard-heading">
          <div>
            <p className="page-eyebrow">User access</p>
            <h1>Create user</h1>
            <p>Add a teammate and choose how they work in the studio.</p>
          </div>
          <div className="dashboard-heading-actions">
            <Button asChild variant="outline">
              <Link to="/users">
                All users
                <ArrowRight size={16} />
              </Link>
            </Button>
          </div>
        </div>

        {created && (
          <div className="success-note" role="status">
            <span>
              <Check size={16} />
            </span>
            <p>
              <strong>{created.name}</strong> was added. They can sign in with {created.email}.
            </p>
            <Link to="/users" className="text-action">
              View users <ArrowRight size={15} />
            </Link>
          </div>
        )}

        <div className="create-layout">
          <form className="dashboard-panel create-form" onSubmit={handleSubmit} noValidate>
            <section className="form-section" aria-labelledby="account-title">
              <div className="form-section-head">
                <h2 id="account-title">Account</h2>
                <p>Their sign-in details.</p>
              </div>
              <div className="field">
                <label htmlFor="name">Full name</label>
                <input
                  id="name"
                  className={fieldClass('name')}
                  placeholder="Jane Doe"
                  autoComplete="off"
                  value={formData.name}
                  onChange={set('name')}
                  onBlur={touch('name')}
                  aria-invalid={Boolean(touched.name && errors.name)}
                />
                {touched.name && errors.name && <small className="field-error">{errors.name}</small>}
              </div>
              <div className="field-row">
                <div className="field">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    className={fieldClass('email')}
                    placeholder="jane@smvec.ac.in"
                    autoComplete="off"
                    value={formData.email}
                    onChange={set('email')}
                    onBlur={touch('email')}
                    aria-invalid={Boolean(touched.email && errors.email)}
                  />
                  {touched.email && errors.email && <small className="field-error">{errors.email}</small>}
                </div>
                <div className="field">
                  <label htmlFor="password">Password</label>
                  <div className="password-field">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      className={fieldClass('password')}
                      placeholder="Minimum 6 characters"
                      autoComplete="new-password"
                      value={formData.password}
                      onChange={set('password')}
                      onBlur={touch('password')}
                      aria-invalid={Boolean(touched.password && errors.password)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                  {formData.password ? (
                    <div className="strength" data-score={strength.score}>
                      <span />
                      <span />
                      <span />
                      <em>{strength.label}</em>
                    </div>
                  ) : (
                    touched.password && errors.password && <small className="field-error">{errors.password}</small>
                  )}
                </div>
              </div>
            </section>

            <section className="form-section" aria-labelledby="role-title">
              <div className="form-section-head">
                <h2 id="role-title">Role</h2>
                <p>What they can do once they sign in.</p>
              </div>
              <div className="choice-grid" role="radiogroup" aria-label="Role">
                {ROLES.map((role) => {
                  const Icon = role.icon;
                  const selected = formData.role === role.id;
                  return (
                    <button
                      type="button"
                      key={role.id}
                      role="radio"
                      aria-checked={selected}
                      data-testid={`role-${role.id}`}
                      className={`choice-card ${selected ? 'is-selected' : ''}`}
                      onClick={() => setFormData({ ...formData, role: role.id })}
                    >
                      <span className="choice-icon">
                        <Icon size={19} strokeWidth={1.7} />
                      </span>
                      <span className="choice-copy">
                        <strong>{role.label}</strong>
                        <small>{role.hint}</small>
                      </span>
                      <span className="choice-check">{selected && <Check size={13} strokeWidth={3} />}</span>
                    </button>
                  );
                })}
              </div>
            </section>

            {formData.role === 'team_member' && (
              <section className="form-section" aria-labelledby="spec-title">
                <div className="form-section-head">
                  <h2 id="spec-title">Specialization</h2>
                  <p>Helps when assigning tasks later.</p>
                </div>
                <div className="chip-radio" role="radiogroup" aria-label="Specialization">
                  {SPECIALIZATIONS.map((spec) => {
                    const Icon = spec.icon;
                    const selected = formData.specialization === spec.id;
                    return (
                      <button
                        type="button"
                        key={spec.id}
                        role="radio"
                        aria-checked={selected}
                        className={selected ? 'is-selected' : ''}
                        onClick={() => setFormData({ ...formData, specialization: spec.id })}
                      >
                        <Icon size={16} strokeWidth={1.7} />
                        {spec.label}
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            <div className="form-actions">
              <Button type="button" variant="ghost" onClick={reset} disabled={loading}>
                Clear
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Creating…' : 'Create user'}
              </Button>
            </div>
          </form>

          <aside className="create-aside" aria-label="Preview and guidance">
            <section className="dashboard-panel preview-panel">
              <p className="preview-label">Preview</p>
              <article className="person-card preview-card">
                <div className="person-top">
                  <UserAvatar id="new-user-preview" name={formData.name || 'New user'} size={58} className="person-avatar" />
                </div>
                <h3 className="person-name">{formData.name.trim() || 'Full name'}</h3>
                <p className="person-email">
                  <Mail size={14} />
                  {formData.email || 'email@example.com'}
                </p>
                <div className="person-pills">
                  <span className={`event-status role-${formData.role}`}>
                    <i />
                    {activeRole?.label}
                  </span>
                  {formData.role === 'team_member' && activeSpec && (
                    <span className="event-status spec-pill">
                      <SpecIcon size={12} />
                      {activeSpec.label}
                    </span>
                  )}
                </div>
              </article>
              <p className="preview-note">Their avatar is generated once the account is created and never changes.</p>
            </section>

            <section className="dashboard-panel guide-panel">
              <div className="panel-heading">
                <div>
                  <h2>Before you add someone</h2>
                </div>
              </div>
              <ul className="guide-list">
                <li>
                  <span>1</span>
                  <p>Use their institutional email so notifications reach the right inbox.</p>
                </li>
                <li>
                  <span>2</span>
                  <p>Choose <strong>Media head</strong> for people who manage events and assign tasks.</p>
                </li>
                <li>
                  <span>3</span>
                  <p>Passwords are set once here and can be changed later from Account settings.</p>
                </li>
              </ul>
              <p className="admin-note">
                <ShieldCheck size={15} />
                Only admins can register users.
              </p>
            </section>
          </aside>
        </div>
      </div>
    </Layout>
  );
};

export default CreateUser;
